import { use, useCallback, useEffect, useRef, useState } from "react";
import { PilotagePilotCardBody } from "../../components/PilotagesPageStates";
import Spacer from "../../../../components/layout/Spacer";
import { Dialog } from "../../../../components/layout/dialogs/Dialog";
import { DialogProperties } from '../../../../components/layout/dialogs/DialogProperties';
import { DialogForm } from "../../../../components/layout/DialogForm";
import { convertExecutedPilotAssignmentApiDtoToIdb, getPilotAssignmentStatusColor, getPilotAssignmentUrl } from "../../pilot-assignment/helpers/PilotAssignmentHelpers";
import moment from "moment";
import PilotAssignmentIdbRepository from "../../../../repositories/idb/PilotAssignmentIdbRepository";
import PilotApiRepository from "../../../../repositories/api/PilotApiRepository";
import { isNumeric } from "../../../../components/helpers/ObjectHelpers";
import { useNavigate, useSearchParams } from "react-router";
import { SuspenseWithErrorBoundry } from "../../../../components/layout/suspense/SuspenseWithErrorBoundry";
import { SuspenseFallback } from "../../../../components/layout/suspense/SuspenseFallback";
import { ErrorBoundryFallback } from "../../../../components/layout/suspense/ErrorBoundryFallback";
import ContentGrid from "../../../../components/layout/ContentGrid";
import uuid from "react-uuid";
import { CardList } from "../../../../components/layout/card/CardList";
import InfiniteScrollComponent from "components/layout/InfiniteScrollComponent";

export const PilotagesExecuted = () => {

    const [contentId] = useState(uuid());
    const [searchParams] = useSearchParams();
    const batchSizeRef = useRef(0);
    const [dataPromise, setDataPromise] = useState(null);
    const navigate = useNavigate();

    const initialize = (forceReset = false) => {
        setDataPromise(fetchData(forceReset));
    }

    useEffect(() => {

        const handleOnOffline = () => {
            initialize();
        }

        window.addEventListener("offline", handleOnOffline);
        window.addEventListener("online", handleOnOffline);

        initialize();

        return () => {
            window.removeEventListener("offline", handleOnOffline);
            window.removeEventListener("online", handleOnOffline);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        (navigator.onLine) &&
        <ContentGrid
            title="Utførte losoppdrag"
            titleIcon="check"
            onPullToRefresh={() => initialize(true)}>
            <SuspenseWithErrorBoundry
                suspenseFallback={<SuspenseFallback/>}
                errorBoundryFallback={<ErrorBoundryFallback mode="text" text="Kunne ikke laste inn losoppdrag..."/>}>
                {
                    dataPromise &&
                    <PilotagesExecutedContent
                        dataPromise={dataPromise}
                        updateUrl={updateUrl}
                        contentId={contentId}
                        batchSizeRef={batchSizeRef}
                    />
                }
            </SuspenseWithErrorBoundry>
        </ContentGrid>
    )

    async function fetchData(forceReset = false) {

        if (!navigator.onLine) return [];

        await cleanUpPilotAssignmentsAsync();

        const result = await fetchPilotAssignmentsAsync(forceReset);

        updateUrl(result.length);

        await PilotAssignmentIdbRepository.cleanUpAsync();

        return result;
    }

    async function cleanUpPilotAssignmentsAsync() {
        const dtos = await PilotAssignmentIdbRepository.getAllExecutedAsync();
        for (const dto of dtos) {
            await PilotAssignmentIdbRepository.deleteAsync(dto);
        }
    }

    async function fetchPilotAssignmentsAsync(forceReset = false) {
        const toTime = moment();
        const batchSize = forceReset ? 0 : getBatchSize();
        batchSizeRef.current = batchSize;

        const response = await PilotApiRepository.getExecutedPilotagesAsync(
            toTime,
            batchSize,
        );

        const result = [];
        const data = await response.json();

        data.forEach((dto) => {
            result.push(convertExecutedPilotAssignmentApiDtoToIdb(dto));
        });

        return result;
    }

    function getBatchSize() {
        const batchSizeParam = searchParams.get("batchSize");
        if (!isNumeric(batchSizeParam)) return 0;

        return batchSizeParam;
    }

    function updateUrl(batchSize = 0, pilotagePilotId = -1) {
        const params = new URLSearchParams(searchParams);
        if(pilotagePilotId !== -1) {
            params.set("pilotagePilotId", pilotagePilotId);
        }
        params.set("batchSize", batchSize);

        navigate({ search: params.toString() }, { replace: true });
    }
}

const PilotagesExecutedContent = ({
    dataPromise,
    updateUrl,
    batchSizeRef
}) => {

    const initialData = use(dataPromise);
    const navigate = useNavigate();
    const [source, setSource] = useState([]);
    const [canFetchMore, setCanFetchMore] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const isFetchingRef = useRef(false);

    function onClick(pilotAssignment) {
        if (navigator.onLine) {
            updateUrl(batchSizeRef.current, pilotAssignment.pilotagePilotId);
            navigate(getPilotAssignmentUrl(pilotAssignment));
        } else {
            setShowDialog(true);
        }
    }

    function createListItem(pilotAssignment) {
        return {
            key: uuid(),
            id: pilotAssignment.pilotagePilotId,
            sortValue: pilotAssignment.pilotage.pilotageDetail.fromTime,
            title: pilotAssignment.pilotage.pilotageShip.shipName,
            subTitle: moment(pilotAssignment.pilotage.pilotageDetail.fromTime).format('DD.MM HH:mm'),
            onClick: () => onClick(pilotAssignment),
            borderColor: getPilotAssignmentStatusColor(pilotAssignment),
            showIndicator: true,
            indicatorPrimaryColor: getPilotAssignmentStatusColor(pilotAssignment),
            content: <PilotagePilotCardBody pilotagePilot={pilotAssignment} />,
        }
    }

    const fetchData = useCallback(async () => {
        if (!navigator.onLine || !canFetchMore || isFetchingRef.current) {
            return;
        }

        isFetchingRef.current = true;

        const lastItem = source[source.length - 1];
        const toTime = lastItem ? moment(lastItem.sortValue).add(-1, "S") : moment();
        const response = await PilotApiRepository
            .getExecutedPilotagesAsync(toTime, 0);

        if (response.ok) {
            const result = await response.json();

            setSource((prevSource) => {
                const existingIds = new Set(prevSource.map(item => item.id));
                const newItems = result
                    .map(dto => createListItem(convertExecutedPilotAssignmentApiDtoToIdb(dto)))
                    .filter(item => !existingIds.has(item.id));

                var updatedSource = [...prevSource, ...newItems];
                batchSizeRef.current = updatedSource.length;
                updateUrl(updatedSource.length);

                return updatedSource;
            });
            setCanFetchMore(result.length > 0);
        }

        isFetchingRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source, canFetchMore, batchSizeRef, updateUrl]);

    // Initialize source from initial data
    useEffect(() => {
        const result = [];
        for (const pilotAssignment of initialData) {
            result.push(createListItem(pilotAssignment));
        }
        setSource(result);
        setCanFetchMore(true);
        isFetchingRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]);

    return (
        <>
            <InfiniteScrollComponent
                items={source}
                hasMore={canFetchMore}
                fetchMore={fetchData}
                scrollableTargetClass="ptr__children"
            >
                <CardList
                    searchParamName="pilotagePilotId"
                    source={source} />
            </InfiniteScrollComponent>
            <Spacer height={20} />

            {
                showDialog &&
                <Dialog
                    properties={{
                        ...DialogProperties,
                        onClose: () => setShowDialog(false),
                        title: "Fullførte",
                        actions: [
                            {
                                onClick: () => setShowDialog(false),
                                icon: "ok",
                                themeColor: "primary",
                                text: "Ok"
                            }
                        ]
                    }}>
                    <DialogForm>
                            <div>
                                Man må være på nett for å se utførte oppdrag.
                            </div>
                    </DialogForm>
                </Dialog>
            }

        </>

    )
}
